import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ActionList, Autocomplete, Banner, Button, Card, Checkbox, IndexTable, Modal, Page, Popover, Select, Spinner, Text, TextField, Thumbnail, Tooltip } from '@shopify/polaris';
import { SafeSaveBar as SaveBar } from '../../app/components/SafeAppBridge';
import { ImageIcon, DeleteIcon, RefreshIcon, SearchIcon } from '@shopify/polaris-icons';
import agent from '../../app/api/agent';
import { DetectedProductFitment, ProductDetails, RealtimeProduct } from '../../app/models/Product';
import { useStore } from '../../app/stores/store';
import { getShopifyStoreHandle } from '../../app/utils/shopifyAdmin';
import type { FitmentCatalogSettings } from '../../app/models/FitmentCatalog';
import {
    getFitmentFieldEmptyOptionLabel,
    getFitmentFieldValue,
    getLegacyFieldValues,
    getLegacyFieldStateKey,
    getSelectedFitmentLabel,
    getVisibleFitmentFields,
    hasAnyFitmentFieldValue,
    hasRequiredFitmentFieldValues,
    isFitmentFieldDisabled,
    normalizeFitmentFieldValues,
    normalizeFitmentKey,
    updateFitmentFieldValue,
    type FitmentFieldValues,
} from '../../app/utils/fitmentFields';

type SelectOption = { label: string; value: string };
type AssignedFitment = ProductDetails['fitments'][number];
type FitmentField = ReturnType<typeof getVisibleFitmentFields>[number];
type AssignedFitmentView = 'grouped' | 'individual';
type AssignedFitmentRow = {
    key: string;
    fitments: AssignedFitment[];
    fieldValues: Record<string, string>;
};
type YearRangeDraft = {
    id: string;
    start: string;
    end: string;
};
type YearGroupEditDraft = {
    rowKey: string;
    fitmentKeys: string[];
    templateFitment: AssignedFitment;
    fieldValues: Record<string, string>;
    ranges: YearRangeDraft[];
};

const productScopeValue = 'product';
const autoScopeValue = 'auto';
const variantScopePrefix = 'variant:';

const getVariantScopeValue = (variantId: number) => `${variantScopePrefix}${variantId}`;

const formatFitmentCountLabel = (count: number) => `${count} ${count === 1 ? 'fitment' : 'fitments'}`;

const getFitmentIdentity = (fitment: AssignedFitment) =>
    String(fitment.id || fitment.tag || JSON.stringify(fitment.fieldValues ?? {}));

const getVariantIdFromScope = (scope: string): number | null => {
    if (!scope.startsWith(variantScopePrefix)) {
        return null;
    }

    const variantId = Number(scope.slice(variantScopePrefix.length));
    return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
};

const sortDetectedFitments = (a: DetectedProductFitment, b: DetectedProductFitment) => {
    const yearCompare = Number(b.year) - Number(a.year);
    if (yearCompare !== 0) {
        return yearCompare;
    }

    const makeCompare = a.make.localeCompare(b.make);
    if (makeCompare !== 0) {
        return makeCompare;
    }

    const modelCompare = a.model.localeCompare(b.model);
    if (modelCompare !== 0) {
        return modelCompare;
    }

    return (a.submodel || '').localeCompare(b.submodel || '');
};

const escapeVehicleTagSegment = (value: string) =>
    value.trim().replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const normalizeFitmentNoteLines = (value: string | string[], preserveBlankLines = false) => {
    const lines = (Array.isArray(value) ? value : value.split(/\r?\n/))
        .map((note) => note.trim());

    return preserveBlankLines ? lines : lines.filter(Boolean);
};

const getFitmentNoteLines = (fitment: ProductDetails['fitments'][number], includeBlankLines = false) =>
    (fitment.notes ?? [])
        .map((note) => note.text ?? '')
        .filter((note) => includeBlankLines || Boolean(note));

const getFitmentNotesText = (fitment: ProductDetails['fitments'][number]) =>
    getFitmentNoteLines(fitment).join('\n');

const getCompactFitmentNotesText = (fitment: ProductDetails['fitments'][number]) =>
    getFitmentNotesText(fitment).replace(/\s*\r?\n\s*/g, '; ');

// The UI edits each note as its own row. Once edited from the PDP, the saved
// notes become Manual.
const buildManualFitmentNotes = (value: string | string[], preserveBlankLines = false) =>
    normalizeFitmentNoteLines(value, preserveBlankLines).map((text, index) => ({
        // Negative IDs only stabilize unsaved React rows; the API persists new IDs.
        id: -(index + 1),
        text,
        source: 'Manual',
        qualifierId: null,
        sortOrder: index + 1,
    }));

type FitmentNotesEditorProps = {
    label: string;
    noteLines: string[];
    disabled?: boolean;
    onChange: (noteLines: string[]) => void;
    onDone?: () => void;
    onClear?: () => void;
};

const FitmentNotesEditor = ({
    label,
    noteLines,
    disabled = false,
    onChange,
    onDone,
    onClear,
}: FitmentNotesEditorProps) => {
    const rows = noteLines.length > 0 ? noteLines : [''];
    const hasNotes = noteLines.some((note) => note.trim());

    const updateLine = (index: number, value: string) => {
        const nextLines = [...rows];
        nextLines[index] = value;
        onChange(nextLines);
    };

    const removeLine = (index: number) => {
        onChange(rows.filter((_, rowIndex) => rowIndex !== index));
    };

    return (
        <div style={{ display: 'grid', gap: 8 }}>
            <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
            {rows.map((value, index) => (
                <div key={`fitment-note-${index}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'start' }}>
                    <TextField
                        label={`${label} ${index + 1}`}
                        labelHidden
                        value={value}
                        onChange={(nextValue) => updateLine(index, nextValue)}
                        autoComplete="off"
                        disabled={disabled}
                        placeholder={`Note ${index + 1}`}
                    />
                    <Button
                        variant="plain"
                        tone="critical"
                        onClick={() => removeLine(index)}
                        disabled={disabled || (rows.length === 1 && !value.trim())}
                    >
                        Remove
                    </Button>
                </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Button variant="plain" onClick={() => onChange([...rows, ''])} disabled={disabled}>
                    + Add another note
                </Button>
                {(onDone || onClear) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {onDone && (
                            <Button variant="plain" onClick={onDone} disabled={disabled}>Done</Button>
                        )}
                        {onClear && hasNotes && (
                            <Button variant="plain" tone="critical" onClick={onClear} disabled={disabled}>Clear notes</Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const stopTableKeyboardPropagation = (event: React.KeyboardEvent) => {
    event.stopPropagation();
};

const normalizeFitmentTags = (fitments: ProductDetails['fitments']) =>
    [...fitments]
        // Unsaved-change detection must include fieldValues, otherwise a
        // custom-field-only row with no Shopify tag would look unchanged.
        .map((fitment) => {
            const fieldSignature = JSON.stringify(fitment.fieldValues ?? {
                year: fitment.year,
                make: fitment.make,
                model: fitment.model,
                submodel: fitment.submodel,
            });

            return `${fitment.tag || ''}:${fieldSignature}:${getFitmentNotesText(fitment)}`;
        })
        .sort((left, right) => left.localeCompare(right));

const buildStableClientTagId = (tag: string) =>
    Math.abs(tag.split('').reduce((hash, character) => ((hash * 31) + character.charCodeAt(0)) | 0, 17));

const buildFitmentTagFromValues = (values: FitmentFieldValues, useYmmPrefix: boolean) => {
    const legacyValues = getLegacyFieldValues(values);
    const legacyTagSegments = [legacyValues.make, legacyValues.model, legacyValues.submodel, legacyValues.year]
        .filter(Boolean)
        .map(escapeVehicleTagSegment);
    const baseTag = legacyTagSegments.join('-');

    return baseTag ? (useYmmPrefix ? `ymm_${baseTag}` : baseTag) : '';
};

const buildDraftFitmentFromValues = (
    values: FitmentFieldValues,
    fields: FitmentField[],
    useYmmPrefix: boolean,
    notes: AssignedFitment['notes'] = [],
    sourceType = 'Manual',
): AssignedFitment => {
    const normalizedValues = normalizeFitmentFieldValues(fields, values);
    const legacyValues = getLegacyFieldValues(normalizedValues);
    const fitmentKey = JSON.stringify(normalizedValues);
    const tag = buildFitmentTagFromValues(normalizedValues, useYmmPrefix);

    return {
        id: buildStableClientTagId(tag || fitmentKey),
        tag,
        year: legacyValues.year,
        make: legacyValues.make,
        model: legacyValues.model,
        submodel: legacyValues.submodel,
        fieldValues: normalizedValues,
        sourceType,
        source: tag || getSelectedFitmentLabel(fields, normalizedValues),
        canRemove: true,
        createdAt: new Date().toISOString(),
        notes: (notes ?? []).map((note) => ({ ...note })),
    };
};

const getProductFitmentFieldValue = (
    fitment: ProductDetails['fitments'][number] | DetectedProductFitment,
    key: ReturnType<typeof getLegacyFieldStateKey>,
) => {
    if (key === 'year') return fitment.year;
    if (key === 'make') return fitment.make;
    if (key === 'model') return fitment.model;
    if (key === 'submodel') return fitment.submodel || '-';
    return '-';
};

const getFitmentDisplayValue = (
    fitment: ProductDetails['fitments'][number] | DetectedProductFitment,
    field: FitmentField,
) => {
    const key = normalizeFitmentKey(field);
    // Prefer generic fieldValues. The legacy columns are only a fallback for
    // Shopify-tag-derived rows and older product fitment responses.
    if ('fieldValues' in fitment && fitment.fieldValues?.[key]) {
        return fitment.fieldValues[key];
    }

    return getProductFitmentFieldValue(fitment, getLegacyFieldStateKey(field));
};

const parseYearValue = (value: string): number | null => {
    const trimmed = value.trim();
    return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

const compareYearDescending = (leftValue: string, rightValue: string) => {
    const leftYear = parseYearValue(leftValue);
    const rightYear = parseYearValue(rightValue);

    if (leftYear != null && rightYear != null && leftYear !== rightYear) {
        return rightYear - leftYear;
    }

    if (leftYear != null && rightYear == null) {
        return -1;
    }

    if (leftYear == null && rightYear != null) {
        return 1;
    }

    return rightValue.localeCompare(leftValue, undefined, { numeric: true, sensitivity: 'base' });
};

const compareYearAscending = (leftValue: string, rightValue: string) =>
    -compareYearDescending(leftValue, rightValue);

const compareYearByConfiguredDirection = (
    leftValue: string,
    rightValue: string,
    yearField?: FitmentField,
) =>
    yearField?.sortDirection === 'Asc'
        ? compareYearAscending(leftValue, rightValue)
        : compareYearDescending(leftValue, rightValue);

const compareFitmentValueAscending = (leftValue: string, rightValue: string) =>
    leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: 'base' });

const getFitmentNotesSignature = (fitment: AssignedFitment) =>
    (fitment.notes ?? [])
        .map((note) => [
            note.text,
            note.source,
            note.qualifierId ?? '',
            note.sortOrder,
        ].join('\u001f'))
        .join('\u001e');

const getAssignedFitmentSearchText = (fitment: AssignedFitment, fields: FitmentField[]) =>
    [
        ...fields.map((field) => getFitmentDisplayValue(fitment, field)),
        getFitmentNotesText(fitment),
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

const isStandardFitmentField = (field: FitmentField, key: string) =>
    (field.key || '').trim().toLowerCase() === key;

const orderFitmentFieldsForPdp = (fields: FitmentField[], yearField?: FitmentField) => {
    if (!yearField) {
        return fields;
    }

    const makeField = fields.find((field) => isStandardFitmentField(field, 'make'));
    const modelField = fields.find((field) => isStandardFitmentField(field, 'model'));
    const prioritizedFields = [makeField, modelField].filter((field): field is FitmentField => !!field);
    const prioritizedSet = new Set(prioritizedFields);

    return [
        ...prioritizedFields,
        ...fields.filter((field) => field !== yearField && !prioritizedSet.has(field)),
        yearField,
    ];
};

const filterAssignedFitments = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
    searchValue: string,
) => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return normalizedSearch
        ? fitments.filter((fitment) => getAssignedFitmentSearchText(fitment, fields).includes(normalizedSearch))
        : fitments;
};

const sortAssignedFitments = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
    yearField?: FitmentField,
) =>
    [...fitments].sort((left, right) => {
        for (const field of fields.filter((field) => field !== yearField)) {
            const comparison = compareFitmentValueAscending(
                getFitmentDisplayValue(left, field),
                getFitmentDisplayValue(right, field),
            );
            if (comparison !== 0) {
                return comparison;
            }
        }

        if (yearField) {
            const yearComparison = compareYearByConfiguredDirection(
                getFitmentDisplayValue(left, yearField),
                getFitmentDisplayValue(right, yearField),
                yearField,
            );
            if (yearComparison !== 0) {
                return yearComparison;
            }
        }

        return getFitmentIdentity(left).localeCompare(getFitmentIdentity(right), undefined, { numeric: true });
    });

const formatYearRange = (values: string[]) => {
    const uniqueValues = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
    if (uniqueValues.length === 0) {
        return '';
    }

    const numericYears = uniqueValues
        .filter((value) => /^\d+$/.test(value))
        .map((value) => Number(value))
        .sort((left, right) => left - right);
    const nonNumericYears = uniqueValues
        .filter((value) => !/^\d+$/.test(value))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));

    const ranges: string[] = [];
    if (numericYears.length > 0) {
        let rangeStart = numericYears[0];
        let previousYear = numericYears[0];

        numericYears.slice(1).forEach((year) => {
            if (year === previousYear + 1) {
                previousYear = year;
                return;
            }

            ranges.push(rangeStart === previousYear ? `${rangeStart}` : `${rangeStart}-${previousYear}`);
            rangeStart = year;
            previousYear = year;
        });

        ranges.push(rangeStart === previousYear ? `${rangeStart}` : `${rangeStart}-${previousYear}`);
    }

    return [...ranges, ...nonNumericYears].join(', ');
};

const buildYearRangeDrafts = (values: string[]): YearRangeDraft[] => {
    const uniqueValues = Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
    const numericYears = uniqueValues
        .filter((value) => /^\d+$/.test(value))
        .map((value) => Number(value))
        .sort((left, right) => left - right);
    const nonNumericYears = uniqueValues
        .filter((value) => !/^\d+$/.test(value))
        .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
    const ranges: YearRangeDraft[] = [];

    if (numericYears.length > 0) {
        let rangeStart = numericYears[0];
        let previousYear = numericYears[0];

        numericYears.slice(1).forEach((year) => {
            if (year === previousYear + 1) {
                previousYear = year;
                return;
            }

            ranges.push({
                id: `${rangeStart}-${previousYear}-${ranges.length}`,
                start: `${rangeStart}`,
                end: `${previousYear}`,
            });
            rangeStart = year;
            previousYear = year;
        });

        ranges.push({
            id: `${rangeStart}-${previousYear}-${ranges.length}`,
            start: `${rangeStart}`,
            end: `${previousYear}`,
        });
    }

    nonNumericYears.forEach((year) => {
        ranges.push({
            id: `${year}-${ranges.length}`,
            start: year,
            end: year,
        });
    });

    return ranges.length > 0 ? ranges : [{ id: 'range-0', start: '', end: '' }];
};

const expandYearRangeDrafts = (ranges: YearRangeDraft[]) => {
    const years: string[] = [];
    const seenYears = new Set<string>();

    for (const range of ranges) {
        const start = range.start.trim();
        const end = range.end.trim();
        if (!start || !end) {
            return { years: [], error: 'Enter a start and end year for each range.' };
        }

        const startYear = parseYearValue(start);
        const endYear = parseYearValue(end);
        if (startYear == null || endYear == null) {
            if (start !== end) {
                return { years: [], error: 'Year ranges must use whole-number years.' };
            }

            if (seenYears.has(start)) {
                return { years: [], error: 'Year ranges cannot overlap.' };
            }

            seenYears.add(start);
            years.push(start);
            continue;
        }

        if (startYear > endYear) {
            return { years: [], error: 'Start year must be before or equal to end year.' };
        }

        for (let year = startYear; year <= endYear; year += 1) {
            const yearValue = `${year}`;
            if (seenYears.has(yearValue)) {
                return { years: [], error: 'Year ranges cannot overlap.' };
            }

            seenYears.add(yearValue);
            years.push(yearValue);
        }
    }

    return { years, error: '' };
};

const buildIndividualYearFitmentRows = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
): AssignedFitmentRow[] =>
    fitments.map((fitment) => ({
        key: getFitmentIdentity(fitment),
        fitments: [fitment],
        fieldValues: fields.reduce<Record<string, string>>((values, field) => {
            values[normalizeFitmentKey(field)] = getFitmentDisplayValue(fitment, field);
            return values;
        }, {}),
    }));

const buildGroupedFitmentRows = (
    fitments: AssignedFitment[],
    fields: FitmentField[],
    yearField: FitmentField,
): AssignedFitmentRow[] => {
    const groupedRows = new Map<string, AssignedFitment[]>();
    const nonYearFields = fields.filter((field) => normalizeFitmentKey(field) !== 'year');

    fitments.forEach((fitment) => {
        const groupKey = [
            ...nonYearFields.map((field) => `${normalizeFitmentKey(field)}=${getFitmentDisplayValue(fitment, field)}`),
            `notes=${getFitmentNotesSignature(fitment)}`,
        ].join('\u001e');

        const group = groupedRows.get(groupKey) ?? [];
        group.push(fitment);
        groupedRows.set(groupKey, group);
    });

    return Array.from(groupedRows.values()).map((group) => {
        const firstFitment = group[0];
        const fieldValues = fields.reduce<Record<string, string>>((values, field) => {
            const key = normalizeFitmentKey(field);
            values[key] = key === 'year'
                ? formatYearRange(group.map((fitment) => getFitmentDisplayValue(fitment, yearField)))
                : getFitmentDisplayValue(firstFitment, field);
            return values;
        }, {});

        return {
            key: group.length === 1
                ? getFitmentIdentity(firstFitment)
                : `group:${group.map(getFitmentIdentity).join('|')}`,
            fitments: group,
            fieldValues,
        };
    });
};

const getAddFitmentYearValues = (startValue: string, endValue: string) => {
    const start = startValue.trim();
    const end = endValue.trim();

    if (!start && !end) {
        return { values: [''], error: '' };
    }

    if (!end || end === start) {
        return { values: [start], error: '' };
    }

    if (!start) {
        return { values: [], error: 'Choose a starting year before choosing Year To.' };
    }

    const startYear = parseYearValue(start);
    const endYear = parseYearValue(end);
    if (startYear == null || endYear == null) {
        return { values: [], error: 'Year ranges must use whole-number years.' };
    }

    if (startYear > endYear) {
        return { values: [], error: 'Year To must be the same as or after the starting year.' };
    }

    return {
        values: Array.from({ length: endYear - startYear + 1 }, (_, index) => `${startYear + index}`),
        error: '',
    };
};

const getAutocompleteOptions = (options: SelectOption[], value: string) => {
    const normalizedValue = value.trim().toLowerCase();
    const usableOptions = options.filter((option) => option.value);
    const matchingOptions = normalizedValue
        ? usableOptions.filter((option) =>
            option.label.toLowerCase().includes(normalizedValue)
            || option.value.toLowerCase().includes(normalizedValue))
        : usableOptions;

    return matchingOptions;
};

const FitmentValueAutocomplete = ({
    label,
    value,
    options,
    disabled,
    loading,
    error,
    placeholder,
    onChange,
}: {
    label: string;
    value: string;
    options: SelectOption[];
    disabled?: boolean;
    loading?: boolean;
    error?: string;
    placeholder?: string;
    onChange: (value: string) => void;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const filteredOptions = getAutocompleteOptions(options, value);

    useEffect(() => {
        containerRef.current
            ?.querySelectorAll<HTMLButtonElement>('.Polaris-TextField__ClearButton')
            .forEach((button) => {
                // Polaris clear buttons are focusable by default. Keeping them
                // out of tab order lets the combobox close before focus moves
                // to the next selector input.
                button.tabIndex = -1;
            });
    }, [value]);

    const textField = (
        <Autocomplete.TextField
            label={label}
            value={value}
            onChange={onChange}
            autoComplete="off"
            disabled={disabled}
            placeholder={placeholder}
            clearButton={!!value}
            onClearButtonClick={() => onChange('')}
            error={error ? true : undefined}
        />
    );

    return (
        <div ref={containerRef}>
            <Autocomplete
                options={filteredOptions}
                selected={value ? [value] : []}
                textField={textField}
                onSelect={(selected) => onChange(selected[0] ?? '')}
                loading={loading}
                emptyState={(
                    <div style={{ padding: 12 }}>
                        <Text as="p" variant="bodySm" tone="subdued">
                            No saved values. Type a new value to use it.
                        </Text>
                    </div>
                )}
            />
        </div>
    );
};

const ProductDetailsPage: React.FC = () => {
    const { id } = useParams();
    const productId = Number(id);
    const navigate = useNavigate();
    const { userStore } = useStore();
    const [fitmentCatalog, setFitmentCatalog] = useState<FitmentCatalogSettings | null>(null);

    const [product, setProduct] = useState<ProductDetails | null>(null);
    const [draftFitments, setDraftFitments] = useState<ProductDetails['fitments']>([]);
    const [draftIsUniversal, setDraftIsUniversal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string>('');
    const [useYmmPrefixForProductTags, setUseYmmPrefixForProductTags] = useState(true);
    const [showRawTags, setShowRawTags] = useState(false);
    const [fitmentActionsOpen, setFitmentActionsOpen] = useState(false);
    const [deleteAllFitmentsModalOpen, setDeleteAllFitmentsModalOpen] = useState(false);
    const [copyQuery, setCopyQuery] = useState('');
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copySourceProducts, setCopySourceProducts] = useState<RealtimeProduct[]>([]);
    const [copySearching, setCopySearching] = useState(false);
    const [includeCopiedFitmentNotes, setIncludeCopiedFitmentNotes] = useState(true);
    const [fitmentScope, setFitmentScope] = useState(autoScopeValue);
    const [assignedFitmentView, setAssignedFitmentView] = useState<AssignedFitmentView>('grouped');
    const [assignedFitmentSearchVisible, setAssignedFitmentSearchVisible] = useState(false);
    const [assignedFitmentSearch, setAssignedFitmentSearch] = useState('');
    const [editingYearGroup, setEditingYearGroup] = useState<YearGroupEditDraft | null>(null);
    const [yearGroupEditError, setYearGroupEditError] = useState('');

    const [selectedFitmentValues, setSelectedFitmentValues] = useState<FitmentFieldValues>({});
    const [selectedFitmentYearTo, setSelectedFitmentYearTo] = useState('');
    const [newFitmentNoteLines, setNewFitmentNoteLines] = useState<string[]>([]);
    const [showNewFitmentNotes, setShowNewFitmentNotes] = useState(false);
    const [editingFitmentNotesKey, setEditingFitmentNotesKey] = useState('');
    const [fieldOptions, setFieldOptions] = useState<Record<string, SelectOption[]>>({});
    const [loadingFieldOptions, setLoadingFieldOptions] = useState(false);
    const visibleFitmentFields = useMemo(() => getVisibleFitmentFields(fitmentCatalog), [fitmentCatalog]);
    const visibleFitmentFieldKey = useMemo(
        () => visibleFitmentFields.map((field) => normalizeFitmentKey(field)).join('|'),
        [visibleFitmentFields],
    );
    const standardYearFitmentField = useMemo(
        () => visibleFitmentFields.find((field) => isStandardFitmentField(field, 'year')),
        [visibleFitmentFields],
    );
    const productDetailsFitmentFields = useMemo(
        () => orderFitmentFieldsForPdp(visibleFitmentFields, standardYearFitmentField),
        [standardYearFitmentField, visibleFitmentFields],
    );
    // The Add button follows the merchant's required-field setup. In a custom
    // catalog with no required fields, one selected value is enough to add.
    const hasVehicleContext = Boolean(
        visibleFitmentFields.length > 0
        && (visibleFitmentFields.some((field) => field.isRequired)
            ? hasRequiredFitmentFieldValues(visibleFitmentFields, selectedFitmentValues)
            : hasAnyFitmentFieldValue(visibleFitmentFields, selectedFitmentValues))
    );

    const loadDetails = useCallback(async () => {
        if (!productId || Number.isNaN(productId)) {
            setError('Invalid product id.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const requestedVariantId = fitmentScope === productScopeValue
                ? 0
                : getVariantIdFromScope(fitmentScope);
            const result = await agent.Products.details(productId, requestedVariantId);
            setProduct(result);
            setDraftFitments(result.fitments || []);
            setEditingFitmentNotesKey('');
            setEditingYearGroup(null);
            setYearGroupEditError('');
            setDraftIsUniversal(result.selectedVariantId ? false : !!result.isUniversal);
            if (fitmentScope === autoScopeValue) {
                setFitmentScope(result.selectedVariantId ? getVariantScopeValue(result.selectedVariantId) : productScopeValue);
            }
        } catch (e: any) {
            setError(e?.message ?? 'Failed to load product details.');
        } finally {
            setLoading(false);
        }
    }, [fitmentScope, productId]);

    useEffect(() => {
        loadDetails();
    }, [loadDetails]);

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settings = await agent.Settings.details();
                setUseYmmPrefixForProductTags(settings.useYmmPrefixForProductTags ?? true);
            } catch {
                setUseYmmPrefixForProductTags(true);
            }
        };

        loadSettings();
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadFitmentCatalog = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (!cancelled) setFitmentCatalog(catalog);
            } catch {
                if (!cancelled) setFitmentCatalog(null);
            }
        };

        loadFitmentCatalog();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setSelectedFitmentValues((current) => normalizeFitmentFieldValues(visibleFitmentFields, current));
    }, [visibleFitmentFieldKey, visibleFitmentFields]);

    useEffect(() => {
        if (!standardYearFitmentField) {
            setSelectedFitmentYearTo('');
        }
    }, [standardYearFitmentField]);

    useEffect(() => {
        let cancelled = false;

        const loadOptions = async () => {
            if (visibleFitmentFields.length === 0) {
                setFieldOptions({});
                return;
            }

            setLoadingFieldOptions(true);
            try {
                // Requery after each selection so later dropdowns are narrowed
                // by earlier selected values, regardless of the field names.
                const normalizedValues = normalizeFitmentFieldValues(visibleFitmentFields, selectedFitmentValues);
                const result = await agent.Vehicles.filterOptions(
                    normalizedValues.year || '',
                    normalizedValues.make || '',
                    '',
                    normalizedValues,
                );

                if (cancelled) return;

                const nextOptions = visibleFitmentFields.reduce<Record<string, SelectOption[]>>((accumulator, field) => {
                    const key = normalizeFitmentKey(field);
                    const values = result.optionsByField?.[key] ?? [];
                    accumulator[key] = [
                        { label: getFitmentFieldEmptyOptionLabel(field, 'select'), value: '' },
                        ...values.map((value) => ({ label: value, value })),
                    ];
                    return accumulator;
                }, {});

                setFieldOptions(nextOptions);
            } catch {
                if (!cancelled) setFieldOptions({});
            } finally {
                if (!cancelled) setLoadingFieldOptions(false);
            }
        };

        loadOptions();
        return () => {
            cancelled = true;
        };
    }, [visibleFitmentFields, selectedFitmentValues]);

    const selectedFitmentYearValue = standardYearFitmentField
        ? getFitmentFieldValue(selectedFitmentValues, standardYearFitmentField)
        : '';
    const addFitmentYearRange = useMemo(
        () => standardYearFitmentField
            ? getAddFitmentYearValues(selectedFitmentYearValue, selectedFitmentYearTo)
            : { values: [''], error: '' },
        [selectedFitmentYearTo, selectedFitmentYearValue, standardYearFitmentField],
    );
    const canAddFitment = hasVehicleContext && !addFitmentYearRange.error;

    const onAddFitment = useCallback(async () => {
        if (!canAddFitment) {
            return;
        }

        const normalizedValues = normalizeFitmentFieldValues(visibleFitmentFields, selectedFitmentValues);
        const yearKey = standardYearFitmentField ? normalizeFitmentKey(standardYearFitmentField) : '';
        const notes = buildManualFitmentNotes(newFitmentNoteLines);
        const yearValues = standardYearFitmentField
            ? addFitmentYearRange.values
            : [''];
        const fitmentsToAdd = yearValues.map((yearValue) => {
            const values = yearKey
                ? normalizeFitmentFieldValues(visibleFitmentFields, { ...normalizedValues, [yearKey]: yearValue })
                : normalizedValues;

            return buildDraftFitmentFromValues(values, visibleFitmentFields, useYmmPrefixForProductTags, notes);
        });

        setDraftFitments((currentFitments) => {
            const existingFieldValueKeys = new Set(currentFitments.map((fitment) => JSON.stringify(fitment.fieldValues ?? {})));
            const newFitments = fitmentsToAdd.filter((fitment) => {
                const key = JSON.stringify(fitment.fieldValues ?? {});
                if (existingFieldValueKeys.has(key)) {
                    return false;
                }

                existingFieldValueKeys.add(key);
                return true;
            });

            return newFitments.length > 0 ? [...currentFitments, ...newFitments] : currentFitments;
        });

        setSelectedFitmentValues({});
        setSelectedFitmentYearTo('');
        setNewFitmentNoteLines([]);
        setShowNewFitmentNotes(false);
    }, [addFitmentYearRange.values, canAddFitment, newFitmentNoteLines, selectedFitmentValues, standardYearFitmentField, useYmmPrefixForProductTags, visibleFitmentFields]);

    const onRemoveFitments = useCallback((fitmentKeys: string[], rowKey: string) => {
        const fitmentKeySet = new Set(fitmentKeys);
        setDraftFitments((currentFitments) =>
            currentFitments.filter((fitment) => !fitmentKeySet.has(getFitmentIdentity(fitment))));
        setEditingFitmentNotesKey((current) => current === rowKey ? '' : current);
        setEditingYearGroup((current) => current?.rowKey === rowKey ? null : current);
    }, []);

    const onUpdateFitmentNotes = useCallback((fitmentKeys: string[], value: string | string[]) => {
        const fitmentKeySet = new Set(fitmentKeys);
        // Editing an imported note intentionally replaces that assignment's note
        // list with Manual notes when the merchant hits Save.
        setDraftFitments((currentFitments) =>
            currentFitments.map((fitment) =>
                fitmentKeySet.has(getFitmentIdentity(fitment))
                    ? { ...fitment, notes: buildManualFitmentNotes(value, true) }
                    : fitment));
    }, []);

    const onClearFitmentNotes = useCallback((fitmentKeys: string[], rowKey: string) => {
        const fitmentKeySet = new Set(fitmentKeys);
        setDraftFitments((currentFitments) =>
            currentFitments.map((fitment) =>
                fitmentKeySet.has(getFitmentIdentity(fitment))
                    ? { ...fitment, notes: [] }
                    : fitment));
        setEditingFitmentNotesKey((current) => current === rowKey ? '' : current);
    }, []);

    const onOpenEditYearGroup = useCallback((row: AssignedFitmentRow, yearField: FitmentField) => {
        setEditingYearGroup({
            rowKey: row.key,
            fitmentKeys: row.fitments.map(getFitmentIdentity),
            templateFitment: row.fitments[0],
            fieldValues: row.fieldValues,
            ranges: buildYearRangeDrafts(row.fitments.map((fitment) => getFitmentDisplayValue(fitment, yearField))),
        });
        setYearGroupEditError('');
        setEditingFitmentNotesKey('');
    }, []);

    const onUpdateYearGroupRange = useCallback((rangeId: string, field: 'start' | 'end', value: string) => {
        setEditingYearGroup((current) => current
            ? {
                ...current,
                ranges: current.ranges.map((range) =>
                    range.id === rangeId ? { ...range, [field]: value } : range),
            }
            : current);
        setYearGroupEditError('');
    }, []);

    const onAddYearGroupRange = useCallback(() => {
        setEditingYearGroup((current) => current
            ? {
                ...current,
                ranges: [
                    ...current.ranges,
                    { id: `range-${Date.now()}`, start: '', end: '' },
                ],
            }
            : current);
        setYearGroupEditError('');
    }, []);

    const onRemoveYearGroupRange = useCallback((rangeId: string) => {
        setEditingYearGroup((current) => current
            ? {
                ...current,
                ranges: current.ranges.length > 1
                    ? current.ranges.filter((range) => range.id !== rangeId)
                    : current.ranges,
            }
            : current);
        setYearGroupEditError('');
    }, []);

    const onSaveYearGroupEdit = useCallback(() => {
        if (!editingYearGroup) {
            return;
        }

        const result = expandYearRangeDrafts(editingYearGroup.ranges);
        if (result.error) {
            setYearGroupEditError(result.error);
            return;
        }

        if (result.years.length === 0) {
            setYearGroupEditError('Add at least one year range.');
            return;
        }

        const templateValues = editingYearGroup.templateFitment.fieldValues ?? {
            year: editingYearGroup.templateFitment.year,
            make: editingYearGroup.templateFitment.make,
            model: editingYearGroup.templateFitment.model,
            submodel: editingYearGroup.templateFitment.submodel,
        };
        const replacementFitments = result.years.map((year) =>
            buildDraftFitmentFromValues(
                { ...templateValues, year },
                visibleFitmentFields,
                useYmmPrefixForProductTags,
                editingYearGroup.templateFitment.notes,
            ));
        const fitmentKeySet = new Set(editingYearGroup.fitmentKeys);

        setDraftFitments((currentFitments) => [
            ...currentFitments.filter((fitment) => !fitmentKeySet.has(getFitmentIdentity(fitment))),
            ...replacementFitments,
        ]);
        setEditingYearGroup(null);
        setYearGroupEditError('');
    }, [editingYearGroup, useYmmPrefixForProductTags, visibleFitmentFields]);

    const onDiscardChanges = useCallback(() => {
        setDraftFitments(product?.fitments || []);
        setDraftIsUniversal(product?.selectedVariantId ? false : !!product?.isUniversal);
        setEditingFitmentNotesKey('');
        setEditingYearGroup(null);
        setYearGroupEditError('');
        setError('');
    }, [product?.fitments, product?.isUniversal, product?.selectedVariantId]);

    const onDeleteAllFitments = useCallback(() => {
        setDraftFitments([]);
        setEditingFitmentNotesKey('');
        setEditingYearGroup(null);
        setYearGroupEditError('');
        setDeleteAllFitmentsModalOpen(false);
    }, []);

    const onToggleUniversal = useCallback((checked: boolean) => {
        setDraftIsUniversal(checked);
    }, []);

    const onSearchCopySource = useCallback(async () => {
        if (!copyQuery.trim()) {
            setCopySourceProducts([]);
            return;
        }

        setCopySearching(true);
        setError('');
        try {
            const response = await agent.Products.listRealtime(copyQuery.trim(), '', 20);
            setCopySourceProducts(
                response.products.filter((candidate: RealtimeProduct) => candidate.id !== productId),
            );
        } catch (e: any) {
            setError(e?.message ?? 'Failed to search products.');
            setCopySourceProducts([]);
        } finally {
            setCopySearching(false);
        }
    }, [copyQuery, productId]);

    useEffect(() => {
        if (!copyModalOpen) {
            return;
        }

        if (!copyQuery.trim()) {
            setCopySourceProducts([]);
            setCopySearching(false);
            return;
        }

        // Debounce the modal search so typing feels responsive without firing a
        // realtime Shopify query on every keypress.
        const timeoutId = window.setTimeout(() => {
            onSearchCopySource();
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [copyModalOpen, copyQuery, onSearchCopySource]);

    const onOpenCopyModal = useCallback(() => {
        setIncludeCopiedFitmentNotes(true);
        setCopyModalOpen(true);
    }, []);

    const onCopyFitments = useCallback(async (sourceProductId: number) => {
        setSubmitting(true);
        setError('');
        try {
            const sourceProduct = await agent.Products.details(sourceProductId, 0);
            if (sourceProduct.isUniversal) {
                setError('Cannot copy fitments from a universal product.');
                return;
            }

            // Copy the source product's current Shopify-derived fitment tags into
            // the local draft only. Persistence still waits for Save.
            setDraftFitments(sourceProduct.fitments.map((fitment) => ({
                ...fitment,
                id: buildStableClientTagId(fitment.tag),
                notes: includeCopiedFitmentNotes
                    ? (fitment.notes ?? []).map((note) => ({ ...note }))
                    : [],
            })));
            setDraftIsUniversal(false);
            setCopyModalOpen(false);
        } catch (e: any) {
            setError(e?.message ?? 'Failed to copy fitments from the selected product.');
        } finally {
            setSubmitting(false);
        }
    }, [includeCopiedFitmentNotes]);

    const onSaveChanges = useCallback(async () => {
        if (!product) {
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            const selectedVariantId = product.selectedVariantId ?? null;
            // Product scope can update Shopify tags. Variant scope only updates
            // the relational fitment table because Shopify variants have no tags.
            await agent.Products.saveFitmentTags(product.id, {
                variantId: selectedVariantId,
                vehicleTags: selectedVariantId || draftIsUniversal ? [] : draftFitments.map((fitment) => fitment.tag).filter(Boolean),
                isUniversal: !selectedVariantId && draftIsUniversal,
                fitments: !selectedVariantId && draftIsUniversal
                    ? []
                    : draftFitments.map((fitment) => ({
                        year: fitment.year,
                        make: fitment.make,
                        model: fitment.model,
                        submodel: fitment.submodel,
                        fieldValues: fitment.fieldValues ?? {
                            year: fitment.year,
                            make: fitment.make,
                            model: fitment.model,
                            submodel: fitment.submodel,
                        },
                        notes: (fitment.notes ?? [])
                            .map((note) => note.text)
                            .filter(Boolean),
                    })),
            });
            await loadDetails();
        } catch (e: any) {
            setError(e?.message ?? 'Failed to save fitments.');
        } finally {
            setSubmitting(false);
        }
    }, [draftFitments, draftIsUniversal, loadDetails, product]);

    const onImportDetectedFitments = useCallback(() => {
        if (!product?.detectedFitments?.length) {
            return;
        }

        setDraftFitments((currentFitments) => {
            const currentTags = new Set(currentFitments.map((fitment) => fitment.tag.toLowerCase()));
            const importedFitments = product.detectedFitments
                .filter((fitment) => !currentTags.has(fitment.canonicalTag.toLowerCase()))
                .map((fitment) => ({
                    id: buildStableClientTagId(fitment.canonicalTag),
                    tag: fitment.canonicalTag,
                    year: fitment.year,
                    make: fitment.make,
                    model: fitment.model,
                    submodel: fitment.submodel,
                    fieldValues: {
                        year: fitment.year,
                        make: fitment.make,
                        model: fitment.model,
                        submodel: fitment.submodel,
                    },
                    sourceType: 'Detected Tag',
                    source: fitment.sourceTag,
                    canRemove: true,
                    createdAt: new Date().toISOString(),
                    notes: [],
                }));

            return [...currentFitments, ...importedFitments];
        });
    }, [product?.detectedFitments]);

    const filteredFitments = useMemo(
        () => filterAssignedFitments(draftFitments, productDetailsFitmentFields, assignedFitmentSearch),
        [assignedFitmentSearch, draftFitments, productDetailsFitmentFields],
    );
    const sortedFitments = useMemo(
        () => sortAssignedFitments(filteredFitments, productDetailsFitmentFields, standardYearFitmentField),
        [filteredFitments, productDetailsFitmentFields, standardYearFitmentField],
    );
    const effectiveAssignedFitmentView: AssignedFitmentView = standardYearFitmentField ? assignedFitmentView : 'individual';
    const assignedFitmentRows = useMemo(
        () => effectiveAssignedFitmentView === 'grouped' && standardYearFitmentField
            ? buildGroupedFitmentRows(sortedFitments, productDetailsFitmentFields, standardYearFitmentField)
            : buildIndividualYearFitmentRows(sortedFitments, productDetailsFitmentFields),
        [effectiveAssignedFitmentView, sortedFitments, productDetailsFitmentFields, standardYearFitmentField],
    );
    const isAssignedFitmentsLoading = loading || (!product && !error);
    const hasAssignedFitmentRefinements = assignedFitmentSearch.trim().length > 0;
    const draftTagSet = new Set(draftFitments.map((fitment) => fitment.tag.toLowerCase()));
    const selectedVariantId = product?.selectedVariantId ?? null;
    const isVariantScope = !!selectedVariantId;
    const variantsWithFitments = (product?.variants ?? []).filter((variant) => variant.fitmentCount > 0);
    const showFitmentScopeSelector = (product?.variants?.length ?? 0) > 1
        || ((product?.productLevelFitmentCount ?? 0) > 0 && variantsWithFitments.length > 0);
    const importableDetectedFitments = (product?.detectedFitments || [])
        .filter((fitment) => !draftTagSet.has(fitment.canonicalTag.toLowerCase()))
        .sort(sortDetectedFitments);
    const hasUnsavedChanges =
        normalizeFitmentTags(draftFitments).join('|') !== normalizeFitmentTags(product?.fitments || []).join('|')
        || (!isVariantScope && draftIsUniversal !== !!product?.isUniversal);
    const fitmentScopeOptions = [
        {
            label: `Product-level fitments (${formatFitmentCountLabel(product?.productLevelFitmentCount ?? 0)})`,
            value: productScopeValue,
        },
        ...((product?.variants ?? []).map((variant) => {
            let label = `Variant ${variant.id}`;
            if (variant.sku && variant.title) {
                label = `${variant.sku} - ${variant.title} variant`;
            } else if (variant.title) {
                label = `${variant.title} variant`;
            } else if (variant.sku) {
                label = `SKU ${variant.sku} variant`;
            }

            label = `${label} (${formatFitmentCountLabel(variant.fitmentCount)})`;

            return {
                label,
                value: getVariantScopeValue(variant.id),
            };
        })),
    ];
    const selectedScopeValue = selectedVariantId ? getVariantScopeValue(selectedVariantId) : productScopeValue;
    const shopHandle = getShopifyStoreHandle(userStore.user?.shopDomain);
    const shopifyAdminProductUrl = shopHandle && product?.id
        ? `https://admin.shopify.com/store/${shopHandle}/products/${product.id}`
        : '';
    const fitmentSelectorConfigured = visibleFitmentFields.length > 0;
    const editingYearGroupReadonlyFields = editingYearGroup
        ? productDetailsFitmentFields.filter((field) => field !== standardYearFitmentField)
        : [];
    return (
        <>
            <SaveBar id="product-fitments-save-bar" open={hasUnsavedChanges}>
                <button onClick={onDiscardChanges} disabled={submitting || loading}>
                    Discard
                </button>
                <button variant="primary" onClick={onSaveChanges} disabled={submitting || loading} loading={submitting}>
                    Save
                </button>
            </SaveBar>

            <Page
                fullWidth
                title={product?.title || 'Product Details'}
                backAction={{ content: 'Products', onAction: () => navigate('/products') }}
            >
                {error && (
                    <div style={{ marginBottom: 12 }}>
                        <Banner title="Error" tone="critical">{error}</Banner>
                    </div>
                )}

                <Modal
                    open={copyModalOpen}
                    onClose={() => setCopyModalOpen(false)}
                    title="Copy fitments from another product"
                >
                    <Modal.Section>
                        <div style={{ display: 'grid', gap: 16 }}>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'end' }}>
                                <div style={{ flex: 1 }}>
                                    <TextField
                                        label="Search products"
                                        value={copyQuery}
                                        onChange={setCopyQuery}
                                        autoComplete="off"
                                        placeholder="Search by title, SKU, or vendor"
                                    />
                                </div>
                            </div>
                            <Checkbox
                                label="Include fitment notes"
                                checked={includeCopiedFitmentNotes}
                                onChange={setIncludeCopiedFitmentNotes}
                                disabled={submitting || loading}
                            />

                            <div style={{ display: 'grid', gap: 8 }}>
                                {/* Keep the copy picker lightweight so the modal reads like a compact product list. */}
                                {copySourceProducts.map((candidate) => (
                                    <button
                                        key={candidate.id}
                                        type="button"
                                        onClick={() => onCopyFitments(candidate.id)}
                                        disabled={submitting || loading}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '56px minmax(0, 1fr) auto auto',
                                            gap: 12,
                                            alignItems: 'center',
                                            padding: 12,
                                            border: '1px solid #d0d5dd',
                                            borderRadius: 8,
                                            background: '#fff',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {candidate.featuredImage ? (
                                            <Thumbnail source={candidate.featuredImage} alt={candidate.title} size="small" />
                                        ) : (
                                            <Thumbnail source={ImageIcon} alt={candidate.title} size="small" />
                                        )}
                                        <div style={{ minWidth: 0 }}>
                                            <Text as="p" variant="bodyMd" fontWeight="medium">{candidate.title}</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">SKU: {candidate.sku || '-'}</Text>
                                        </div>
                                        <Text as="p" variant="bodySm" tone="subdued">{candidate.fitmentCount} fitments</Text>
                                        <Text as="p" variant="bodySm">Copy</Text>
                                    </button>
                                ))}

                                {!copySearching && copyQuery.trim() && copySourceProducts.length === 0 && (
                                    <Text as="p" variant="bodyMd" tone="subdued">
                                        No matching products found.
                                    </Text>
                                )}
                            </div>
                        </div>
                    </Modal.Section>
                </Modal>

                <Modal
                    open={deleteAllFitmentsModalOpen}
                    onClose={() => setDeleteAllFitmentsModalOpen(false)}
                    title="Delete all fitments"
                    primaryAction={{
                        content: 'Delete all fitments',
                        destructive: true,
                        onAction: onDeleteAllFitments,
                        disabled: draftFitments.length === 0 || submitting || loading,
                    }}
                    secondaryActions={[{
                        content: 'Cancel',
                        onAction: () => setDeleteAllFitmentsModalOpen(false),
                        disabled: submitting || loading,
                    }]}
                >
                    <Modal.Section>
                        <Text as="p">
                            Delete all assigned fitments from this product draft? This change is not saved until you click Save.
                        </Text>
                    </Modal.Section>
                </Modal>

                <Modal
                    open={!!editingYearGroup}
                    onClose={() => {
                        setEditingYearGroup(null);
                        setYearGroupEditError('');
                    }}
                    title="Edit group years"
                    primaryAction={{
                        content: 'Apply year changes',
                        onAction: onSaveYearGroupEdit,
                        disabled: submitting || loading || !editingYearGroup,
                    }}
                    secondaryActions={[{
                        content: 'Cancel',
                        onAction: () => {
                            setEditingYearGroup(null);
                            setYearGroupEditError('');
                        },
                        disabled: submitting || loading,
                    }]}
                >
                    <Modal.Section>
                        {editingYearGroup && (
                            <div style={{ display: 'grid', gap: 16 }}>
                                {yearGroupEditError && (
                                    <Banner title="Invalid year range" tone="critical">
                                        {yearGroupEditError}
                                    </Banner>
                                )}

                                {editingYearGroupReadonlyFields.length > 0 && (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, padding: 12, border: '1px solid #e3e3e3', borderRadius: 8, background: '#fafafa' }}>
                                        {editingYearGroupReadonlyFields.map((field) => {
                                            const key = normalizeFitmentKey(field);
                                            return (
                                                <div key={key} style={{ display: 'grid', gap: 2, minWidth: 0 }}>
                                                    <Text as="span" variant="bodySm" tone="subdued">{field.label}</Text>
                                                    <Text as="span" variant="bodyMd">{editingYearGroup.fieldValues[key] || '-'}</Text>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div style={{ display: 'grid', gap: 12 }}>
                                    {editingYearGroup.ranges.map((range) => (
                                        <div
                                            key={range.id}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
                                                gap: 12,
                                                alignItems: 'end',
                                            }}
                                        >
                                            <TextField
                                                label="Start year"
                                                value={range.start}
                                                onChange={(value) => onUpdateYearGroupRange(range.id, 'start', value)}
                                                autoComplete="off"
                                                type="number"
                                            />
                                            <TextField
                                                label="End year"
                                                value={range.end}
                                                onChange={(value) => onUpdateYearGroupRange(range.id, 'end', value)}
                                                autoComplete="off"
                                                type="number"
                                            />
                                            <Button
                                                variant="plain"
                                                tone="critical"
                                                onClick={() => onRemoveYearGroupRange(range.id)}
                                                disabled={editingYearGroup.ranges.length <= 1}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    ))}
                                </div>

                                <div>
                                    <Button variant="plain" onClick={onAddYearGroupRange}>
                                        + Add range
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Modal.Section>
                </Modal>

                <Card>
                    <div style={{ display: 'grid', gap: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                            {product?.featuredImage ? (
                                <Thumbnail source={product.featuredImage} alt={product.title} size="medium" />
                            ) : (
                                <Thumbnail source={ImageIcon} alt={product?.title ?? 'Product image'} size="medium" />
                            )}
                            <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                    <Text as="p" variant="bodyMd">SKU: {product?.sku || '-'}</Text>
                                    <Text as="p" variant="bodyMd">Vendor: {product?.vendor || '-'}</Text>
                                    {product?.isAtdProduct && (
                                        <Tooltip content="ATD source product synced with Shopify">
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#005bd3' }}>
                                                <RefreshIcon width={16} height={16} aria-hidden="true" />
                                                <Text as="span" variant="bodyMd">ATD synced</Text>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <Text as="p" variant="bodyMd">{draftIsUniversal ? 'Universal product' : `Total fitments: ${product?.totalFitmentCount ?? draftFitments.length}`}</Text>
                                </div>
                                {showFitmentScopeSelector && (
                                    <div style={{ maxWidth: 520 }}>
                                        <Select
                                            label="Fitments for"
                                            labelInline
                                            options={fitmentScopeOptions}
                                            value={selectedScopeValue}
                                            disabled={loading || submitting || hasUnsavedChanges}
                                            onChange={(value) => {
                                                setError('');
                                                setFitmentScope(value);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {shopifyAdminProductUrl && (
                                <Button url={shopifyAdminProductUrl} target="_blank" variant="plain">
                                    Shopify admin
                                </Button>
                            )}
                        </div>
                    </div>
                    </div>
                </Card>

                <div style={{ marginTop: 16 }}>
                    <Card>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                            <div style={{ display: 'grid', gap: 4 }}>
                                <Text as="h3" variant="headingMd">Fitments</Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Manage the product&apos;s universal status and assigned fitment values.
                                </Text>
                            </div>
                            <Popover
                                active={fitmentActionsOpen}
                                activator={
                                    <Button
                                        disclosure
                                        onClick={() => setFitmentActionsOpen((current) => !current)}
                                        disabled={submitting || loading}
                                    >
                                        More actions
                                    </Button>
                                }
                                onClose={() => setFitmentActionsOpen(false)}
                            >
                                <ActionList
                                    items={[
                                        {
                                            content: 'Copy fitments from product',
                                            disabled: isVariantScope,
                                            onAction: () => {
                                                setFitmentActionsOpen(false);
                                                onOpenCopyModal();
                                            },
                                        },
                                        {
                                            content: showRawTags ? 'Hide tags' : 'Show tags',
                                            disabled: draftIsUniversal,
                                            onAction: () => {
                                                setShowRawTags((current) => !current);
                                                setFitmentActionsOpen(false);
                                            },
                                        },
                                        {
                                            content: 'Delete all fitments',
                                            destructive: true,
                                            disabled: draftFitments.length === 0,
                                            onAction: () => {
                                                setFitmentActionsOpen(false);
                                                setDeleteAllFitmentsModalOpen(true);
                                            },
                                        },
                                    ]}
                                />
                            </Popover>
                        </div>

                        {!isVariantScope && (
                            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #e3e3e3', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Checkbox
                                    label="Mark product as universal"
                                    checked={draftIsUniversal}
                                    onChange={onToggleUniversal}
                                />
                                <Tooltip content="Universal products save the partfinder_universal Shopify tag and do not use a fitment table.">
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            border: '1px solid #8a8a8a',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 12,
                                            color: '#616161',
                                            cursor: 'help',
                                        }}
                                    >
                                        ?
                                    </span>
                                </Tooltip>
                            </div>
                        )}

                        {draftIsUniversal ? (
                            <div style={{ marginTop: 16, padding: 16, border: '1px solid #e3e3e3', borderRadius: 8, background: '#f7f7f7' }}>
                                <Text as="p" variant="bodyMd" tone="subdued">
                                    Universal products are available without individual fitment mappings.
                                </Text>
                            </div>
                        ) : (
                            <>
                                {fitmentSelectorConfigured && (
                                    <div style={{ marginTop: 16, padding: 12, border: '1px solid #e3e3e3', borderRadius: 8, background: '#fafafa' }}>
                                        <Text as="h4" variant="headingSm">Add fitment</Text>
                                        <div className="pf-fitment-selector pf-fitment-selector--flush">
                                            {productDetailsFitmentFields.map((field) => {
                                                const key = normalizeFitmentKey(field);
                                                const isYearField = field === standardYearFitmentField;
                                                const fieldDisabled = submitting || loading || isFitmentFieldDisabled(productDetailsFitmentFields, selectedFitmentValues, field, false);
                                                return (
                                                    <React.Fragment key={key}>
                                                        <div className="pf-fitment-selector__field">
                                                            <FitmentValueAutocomplete
                                                                label={field.label}
                                                                options={fieldOptions[key] ?? [{ label: getFitmentFieldEmptyOptionLabel(field, 'select'), value: '' }]}
                                                                value={getFitmentFieldValue(selectedFitmentValues, field)}
                                                                disabled={fieldDisabled}
                                                                loading={loadingFieldOptions}
                                                                placeholder={getFitmentFieldEmptyOptionLabel(field, 'select')}
                                                                onChange={(value) => {
                                                                    setSelectedFitmentValues((current) => updateFitmentFieldValue(productDetailsFitmentFields, current, field, value));
                                                                }}
                                                            />
                                                        </div>
                                                        {isYearField && (
                                                            <div className="pf-fitment-selector__field">
                                                                <FitmentValueAutocomplete
                                                                    label="Year To"
                                                                    options={fieldOptions[key] ?? []}
                                                                    value={selectedFitmentYearTo}
                                                                    disabled={fieldDisabled || !selectedFitmentYearValue}
                                                                    loading={loadingFieldOptions}
                                                                    placeholder="Select year to"
                                                                    error={addFitmentYearRange.error}
                                                                    onChange={setSelectedFitmentYearTo}
                                                                />
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                            <div className="pf-fitment-selector__action">
                                                <Button variant="primary" onClick={onAddFitment} disabled={!canAddFitment || submitting || loading}>Add fitment</Button>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: 8 }}>
                                            {showNewFitmentNotes ? (
                                                <FitmentNotesEditor
                                                    label="Fitment notes"
                                                    noteLines={newFitmentNoteLines}
                                                    onChange={setNewFitmentNoteLines}
                                                    disabled={submitting || loading}
                                                />
                                            ) : (
                                                <Button
                                                    variant="plain"
                                                    onClick={() => {
                                                        setShowNewFitmentNotes(true);
                                                        setNewFitmentNoteLines((current) => current.length > 0 ? current : ['']);
                                                    }}
                                                    disabled={submitting || loading}
                                                >
                                                    + Add notes
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div style={{ marginTop: 18 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                        <Text as="h4" variant="headingSm">Assigned fitments</Text>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                            {assignedFitmentSearchVisible && (
                                                <div style={{ width: 320, maxWidth: '100%' }}>
                                                    <TextField
                                                        label="Search assigned fitments"
                                                        labelHidden
                                                        value={assignedFitmentSearch}
                                                        onChange={setAssignedFitmentSearch}
                                                        autoComplete="off"
                                                        placeholder="Search fitments or notes"
                                                        clearButton
                                                        autoFocus
                                                        onClearButtonClick={() => setAssignedFitmentSearch('')}
                                                    />
                                                </div>
                                            )}
                                            {draftFitments.length > 0 && (
                                                <Button
                                                    icon={SearchIcon}
                                                    variant="tertiary"
                                                    pressed={assignedFitmentSearchVisible}
                                                    accessibilityLabel="Search assigned fitments"
                                                    onClick={() => {
                                                        if (assignedFitmentSearchVisible) {
                                                            setAssignedFitmentSearch('');
                                                            setAssignedFitmentSearchVisible(false);
                                                            return;
                                                        }

                                                        setAssignedFitmentSearchVisible(true);
                                                    }}
                                                />
                                            )}
                                            {standardYearFitmentField && draftFitments.length > 0 && (
                                                <div
                                                    aria-label="Assigned fitment view"
                                                    role="group"
                                                    style={{
                                                        display: 'inline-flex',
                                                        gap: 2,
                                                        padding: 3,
                                                        border: '1px solid #d0d5dd',
                                                        borderRadius: 14,
                                                        background: '#f7f7f7',
                                                    }}
                                                >
                                                    {([
                                                        { value: 'grouped' as AssignedFitmentView, label: 'Grouped' },
                                                        { value: 'individual' as AssignedFitmentView, label: 'Individual years' },
                                                    ]).map((option) => {
                                                        const isSelected = effectiveAssignedFitmentView === option.value;
                                                        return (
                                                            <button
                                                                key={option.value}
                                                                type="button"
                                                                aria-pressed={isSelected}
                                                                onClick={() => {
                                                                    setAssignedFitmentView(option.value);
                                                                    setEditingFitmentNotesKey('');
                                                                }}
                                                                style={{
                                                                    minHeight: 34,
                                                                    padding: '6px 18px',
                                                                    border: '1px solid transparent',
                                                                    borderRadius: 11,
                                                                    background: isSelected ? '#fff' : 'transparent',
                                                                    boxShadow: isSelected ? '0 1px 3px rgba(0, 0, 0, 0.18)' : 'none',
                                                                    color: '#202223',
                                                                    cursor: 'pointer',
                                                                    font: 'inherit',
                                                                    fontWeight: isSelected ? 600 : 400,
                                                                    lineHeight: '20px',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {option.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ marginTop: 10 }}>
                                        {assignedFitmentRows.length > 0 ? (
                                            <IndexTable
                                                resourceName={{ singular: 'fitment', plural: 'fitments' }}
                                                itemCount={assignedFitmentRows.length}
                                                headings={[
                                                    ...productDetailsFitmentFields.map((field) => ({ title: field.label })),
                                                    { title: 'Notes' },
                                                    ...(showRawTags ? [{ title: 'Tag' }] : []),
                                                    { title: '' },
                                                ] as any}
                                                selectable={false}
                                            >
                                                {assignedFitmentRows.map((row, index) => {
                                                    const rowFitmentKeys = row.fitments.map(getFitmentIdentity);
                                                    const notesFitment = row.fitments[0];
                                                    const isEditingNotes = editingFitmentNotesKey === row.key;
                                                    const noteLines = getFitmentNoteLines(notesFitment, isEditingNotes);
                                                    const notesText = getFitmentNotesText(notesFitment);
                                                    const compactNotesText = getCompactFitmentNotesText(notesFitment);
                                                    return (
                                                        <IndexTable.Row id={row.key} key={row.key} position={index}>
                                                            {productDetailsFitmentFields.map((field) => (
                                                                <IndexTable.Cell key={`${row.key}-${field.fieldIndex}`}>
                                                                    {row.fieldValues[normalizeFitmentKey(field)]}
                                                                </IndexTable.Cell>
                                                            ))}
                                                            <IndexTable.Cell>
                                                                <div style={{ minWidth: 240, maxWidth: 360 }}>
                                                                    {isEditingNotes ? (
                                                                        <div
                                                                            style={{ display: 'grid', gap: 8 }}
                                                                            onKeyDown={stopTableKeyboardPropagation}
                                                                            onKeyUp={stopTableKeyboardPropagation}
                                                                        >
                                                                            <FitmentNotesEditor
                                                                                label="Fitment notes"
                                                                                noteLines={noteLines}
                                                                                onChange={(value) => onUpdateFitmentNotes(rowFitmentKeys, value)}
                                                                                disabled={submitting || loading}
                                                                                onDone={() => setEditingFitmentNotesKey('')}
                                                                                onClear={() => onClearFitmentNotes(rowFitmentKeys, row.key)}
                                                                            />
                                                                        </div>
                                                                    ) : notesText ? (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => setEditingFitmentNotesKey(row.key)}
                                                                                disabled={submitting || loading}
                                                                                title={notesText}
                                                                                style={{
                                                                                    appearance: 'none',
                                                                                    background: 'transparent',
                                                                                    border: 0,
                                                                                    color: '#005bd3',
                                                                                    cursor: submitting || loading ? 'default' : 'pointer',
                                                                                    font: 'inherit',
                                                                                    maxWidth: 260,
                                                                                    overflow: 'hidden',
                                                                                    padding: 0,
                                                                                    textAlign: 'left',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                }}
                                                                            >
                                                                                {compactNotesText}
                                                                            </button>
                                                                            <Button variant="plain" tone="critical" onClick={() => onClearFitmentNotes(rowFitmentKeys, row.key)} disabled={submitting || loading}>Clear notes</Button>
                                                                        </div>
                                                                    ) : (
                                                                        <Button variant="plain" onClick={() => setEditingFitmentNotesKey(row.key)} disabled={submitting || loading}>
                                                                            + Add notes
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </IndexTable.Cell>
                                                            {showRawTags && (
                                                                <IndexTable.Cell>
                                                                    {row.fitments.length === 1 ? row.fitments[0].tag || '-' : `${row.fitments.length} tags`}
                                                                </IndexTable.Cell>
                                                            )}
                                                            <IndexTable.Cell>
                                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                                                                    {effectiveAssignedFitmentView === 'grouped' && standardYearFitmentField && (
                                                                        <Button
                                                                            variant="tertiary"
                                                                            onClick={() => onOpenEditYearGroup(row, standardYearFitmentField)}
                                                                            disabled={submitting || loading}
                                                                        >
                                                                            Edit group
                                                                        </Button>
                                                                    )}
                                                                    <Button icon={DeleteIcon} variant="tertiary" tone="critical" onClick={() => onRemoveFitments(rowFitmentKeys, row.key)} disabled={submitting || loading}>
                                                                        {effectiveAssignedFitmentView === 'grouped' ? 'Remove group' : 'Remove'}
                                                                    </Button>
                                                                </div>
                                                            </IndexTable.Cell>
                                                        </IndexTable.Row>
                                                    );
                                                })}
                                            </IndexTable>
                                        ) : isAssignedFitmentsLoading ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16, border: '1px solid #e3e3e3', borderRadius: 8, background: '#fff' }}>
                                                <Spinner accessibilityLabel="Loading assigned fitments" size="small" />
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    Loading assigned fitments...
                                                </Text>
                                            </div>
                                        ) : (
                                            <div style={{ padding: 16, border: '1px solid #e3e3e3', borderRadius: 8, background: '#fff' }}>
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {draftFitments.length > 0 && hasAssignedFitmentRefinements
                                                        ? 'No fitments match the current filters.'
                                                        : 'No fitments assigned yet.'}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {!isVariantScope && importableDetectedFitments.length > 0 && (
                                    <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e3e3e3' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                                            <div>
                                                <Text as="h4" variant="headingSm">Detected fitments</Text>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Potential vehicle tags detected from the product&apos;s current Shopify tags.
                                                </Text>
                                            </div>
                                            {/* Import converts detected tags into the shop's current canonical tag format, but still only stages them locally until Save. */}
                                            <Button onClick={onImportDetectedFitments} disabled={submitting || loading}>
                                                Import detected fitments
                                            </Button>
                                        </div>
                                        <IndexTable
                                            resourceName={{ singular: 'detected fitment', plural: 'detected fitments' }}
                                            itemCount={importableDetectedFitments.length}
                                            headings={[
                                                ...productDetailsFitmentFields.map((field) => ({ title: field.label })),
                                                { title: 'Detected tag' },
                                                { title: 'Will import as' },
                                            ] as any}
                                            selectable={false}
                                        >
                                            {importableDetectedFitments.map((fitment, index) => (
                                                <IndexTable.Row id={fitment.canonicalTag} key={fitment.canonicalTag} position={index}>
                                                    {productDetailsFitmentFields.map((field) => (
                                                        <IndexTable.Cell key={`${fitment.canonicalTag}-${field.fieldIndex}`}>
                                                            {getFitmentDisplayValue(fitment, field)}
                                                        </IndexTable.Cell>
                                                    ))}
                                                    <IndexTable.Cell>{fitment.sourceTag}</IndexTable.Cell>
                                                    <IndexTable.Cell>{fitment.canonicalTag}</IndexTable.Cell>
                                                </IndexTable.Row>
                                            ))}
                                        </IndexTable>
                                    </div>
                                )}
                            </>
                        )}
                    </Card>
                </div>
            </Page>
        </>
    );
};

export default observer(ProductDetailsPage);
